import React, { useRef, useState } from 'react';
import { RootState } from '../../../store/store';
import { useSelector } from 'react-redux';
import { updateCurrentUserInfo } from '../../../services/auth';
import { enqueueSnackbar } from 'notistack';
import OraSwitch from '../../CustomMUI/Switch';
import { hapticsImpactLight } from '../../../utils/haptics';
import { InputLabel, MenuItem, Select } from '@mui/material';
import { TriangleRight } from '../../../assets/svgs/TriangleRight';
import useWindowWidth from '../../../hooks/useWindowWidth';

const EmailRemindersSettings: React.FC = () => {
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const [isSubscribedToEmails, setIsSubscribedToEmails] = useState(
    currentUser?.subscribedToEmails || false
  );
  const [hasRemindersEnabled, setHasRemindersEnabled] = useState(
    currentUser?.studyReminders !== 'never'
  );
  const [studyReminderPreference, setStudyReminderPreference] = useState(
    currentUser?.studyReminders === 'never'
      ? 'occasional'
      : currentUser?.studyReminders
  );
  const [sendAtTime, setSendAtTime] = useState(
    currentUser?.studyReminderSendAt || '08:00'
  );

  const windowWidth = useWindowWidth();
  const emailPreferencesRef = useRef<HTMLDivElement>(null);

  const handleEmailPreferencesChange = async (
    studyRemindersPreference: string
  ) => {
    setStudyReminderPreference(studyRemindersPreference);
    const updateUser = await updateCurrentUserInfo({
      studyReminders: studyRemindersPreference,
    });
    if (updateUser) {
      enqueueSnackbar('Reminder preferences updated.', {
        autoHideDuration: 3000,
      });
    }
  };

  const handleToggleEmails = async (checked: boolean) => {
    setIsSubscribedToEmails(checked);
    const updateUser = await updateCurrentUserInfo({
      subscribedToEmails: checked,
    });
    if (updateUser) {
      enqueueSnackbar(checked ? 'Emails enabled.' : 'Emails disabled.', {
        autoHideDuration: 3000,
      });
    }
  };

  const handleToggleReminders = async (checked: boolean) => {
    setHasRemindersEnabled(checked);
    const newValue = checked ? studyReminderPreference : 'never';
    const updateUser = await updateCurrentUserInfo({
      studyReminders: newValue,
    });
    if (updateUser) {
      enqueueSnackbar('Reminder preferences updated.', {
        autoHideDuration: 3000,
      });
    }
  };

  const updateSendAtTime = async (e: React.FocusEvent<HTMLInputElement>) => {
    // Round to nearest half hour
    const [hours, minutes] = e.target.value.split(':').map(Number);
    const roundedMinutes = Math.round(minutes / 30) * 30;
    const adjustedHours = hours + Math.floor(roundedMinutes / 60);
    const finalMinutes = roundedMinutes % 60;
    const formattedTime = `${String(adjustedHours).padStart(2, '0')}:${String(finalMinutes).padStart(2, '0')}`;

    setSendAtTime(formattedTime);
    const updateUser = await updateCurrentUserInfo({
      studyReminderSendAt: formattedTime,
    });
    if (updateUser) {
      enqueueSnackbar('Reminder preferences updated.', {
        autoHideDuration: 3000,
      });
    }
  };

  return (
    <div className="block block--settings email-reminders-settings">
      <div className="block_body">
        <h3
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
          ref={emailPreferencesRef}
        >
          <span>Email &amp; Reminders</span>
          <OraSwitch
            checked={isSubscribedToEmails}
            onChange={(e) => handleToggleEmails(e.target.checked)}
            onClick={() => {
              hapticsImpactLight();
            }}
          />
        </h3>
        <div
          className="study-reminder-preferences"
          onClick={() => {
            if (!isSubscribedToEmails) {
              if (emailPreferencesRef.current) {
                emailPreferencesRef.current.classList.add('shake');
              }
              setTimeout(() => {
                if (emailPreferencesRef.current) {
                  emailPreferencesRef.current.classList.remove('shake');
                }
              }, 500);
            }
          }}
        >
          <div
            className={`study-reminder-preferences_inner ${!isSubscribedToEmails ? 'disabled' : ''}`}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <InputLabel id="email-preference">Study Reminders</InputLabel>
              <OraSwitch
                checked={hasRemindersEnabled}
                onChange={(e) => handleToggleReminders(e.target.checked)}
                onClick={() => {
                  hapticsImpactLight();
                }}
              />
            </div>
            <div className="study-reminder-input-wrapper">
              <Select
                labelId="email-preferences"
                id="email-preferences"
                value={studyReminderPreference}
                onChange={(e) => {
                  const newValue = e.target.value;
                  handleEmailPreferencesChange(newValue);
                }}
                style={windowWidth > 560 ? { width: '50%' } : { width: '100%' }}
                onClick={hapticsImpactLight}
                disabled={!hasRemindersEnabled}
              >
                <MenuItem value="daily">
                  <TriangleRight />
                  Daily
                </MenuItem>
                <MenuItem value="occasional">
                  <TriangleRight />
                  Occasional
                </MenuItem>
              </Select>
              <div className="time-picker-wrapper">
                <div>at</div>
                <div
                  className={`time-picker ${!hasRemindersEnabled ? 'disabled' : ''}`}
                >
                  <input
                    type="time"
                    id="study-reminder-send-time"
                    name="study-reminder-send-time"
                    required
                    readOnly={!hasRemindersEnabled}
                    value={sendAtTime}
                    step="1800"
                    onChange={(e) => setSendAtTime(e.target.value)}
                    onBlur={(e) => updateSendAtTime(e)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailRemindersSettings;
